import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  CheckCircle2,
  Truck,
  Sparkles,
  HeartHandshake,
  Users,
  Cake,
  IceCream2,
  PartyPopper,
  Mic2,
  ArrowRight,
  Phone,
  Mail,
} from "lucide-react";
import { api } from "../lib/api";
import { BRAND } from "../lib/brand";
import TrailerSchedule from "../components/TrailerSchedule";
import { usePageMeta } from "../hooks/usePageMeta";
import { PAGE_SEO } from "../lib/seo";

const EVENT_TYPES = [
  { id: "wedding",   label: "Weddings",         icon: HeartHandshake, blurb: "Late-night dessert tables, cocktail-hour treats and a picture-perfect trailer moment your guests won't forget." },
  { id: "corporate", label: "Corporate Events", icon: Users,          blurb: "Brand launches, conferences and staff parties. We cater anywhere from 30 to 100+ guests." },
  { id: "birthday",  label: "Birthdays",        icon: Cake,           blurb: "Milestone birthdays, sweet sixteens and grown-up parties. Custom flavours on request." },
  { id: "private",   label: "Private Parties",  icon: PartyPopper,    blurb: "Hens, baby showers, anniversaries, graduations — anywhere worth celebrating." },
  { id: "festival",  label: "Festivals & Markets", icon: Mic2,        blurb: "Pull a crowd at your festival, market or community event. High-volume ready." },
  { id: "other",     label: "Something Else?",  icon: Sparkles,       blurb: "If it's not on this list, we've probably done it. Tell us what you've got in mind." },
];

const PACKAGES = [
  { title: "Handcrafted Rolled Ice Cream", desc: "Made before your eyes — from a simple cream base to your very own masterpiece, hand-rolled to order on a frozen plate.", icon: IceCream2 },
  { title: "Live Trailer Service", desc: "Our trailer parks on-site, freshly serving milkshakes, waffles and rolled ice cream to your guests.", icon: Truck },
  { title: "Custom Menus",         desc: "Tell us your theme, dietary needs or favourite flavours — we'll build a menu around it.", icon: Sparkles },
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  eventType: "",
  eventDate: "",
  guests: "",
  location: "",
  details: "",
};

const Events = () => {
  usePageMeta(PAGE_SEO.events);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);
    try {
      const message = [
        `Event type: ${form.eventType || "(not specified)"}`,
        `Event date: ${form.eventDate || "(not specified)"}`,
        `Estimated guests: ${form.guests || "(not specified)"}`,
        `Location: ${form.location || "(not specified)"}`,
        "",
        "Details:",
        form.details || "(none provided)",
      ].join("\n");
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: `Event Booking · ${form.eventType || "General"}`,
        message,
      });
      setSuccess(true);
      setForm(initialForm);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send your enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="page-events">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage:
              "linear-gradient(to bottom, rgba(5,5,8,0.65) 0%, rgba(5,5,8,0.9) 60%, rgba(5,5,8,1) 100%), url(" + BRAND.trailer + ")",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 flex flex-col gap-6 fade-up">
            <span className="inline-flex items-center gap-2 self-start text-[0.7rem] uppercase tracking-[0.3em] text-[#ff1a8c]">
              <Truck className="w-3.5 h-3.5" /> Our mobile dessert trailer
            </span>
            <h1
              data-testid="events-hero-headline"
              className="font-script text-5xl sm:text-6xl lg:text-[6.5rem] leading-[0.95] neon-gradient-text"
            >
              Book Us For Your<br />Next Event!
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl">
              From weddings and corporate functions to birthdays and festivals — our vintage-style
              caravan trailer rolls up, plugs in, and turns your event into the sweetest night of
              the year.
            </p>
            <div className="flex flex-wrap gap-4 mt-2">
              <a href="#enquire" data-testid="events-hero-enquire-button" className="neon-btn">
                Send Enquiry <ArrowRight className="w-4 h-4" />
              </a>
              <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} data-testid="events-hero-call-button" className="neon-btn neon-btn--blue">
                <Phone className="w-4 h-4" /> Call {BRAND.phone}
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up delay-2">
            <div className="aspect-[4/5] rounded-[2rem] overflow-hidden neon-frame-pink">
              <img
                src={BRAND.trailer}
                alt="The Just Dessert Me mobile dessert trailer at an event"
                data-testid="events-hero-image"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* THIS WEEK'S SCHEDULE */}
      <TrailerSchedule />

      {/* MEET THE TRAILER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="relative rounded-[2rem] overflow-hidden neon-frame-blue">
              <img
                src={BRAND.trailer}
                alt="Mobile dessert trailer serving guests under festoon lights"
                data-testid="events-trailer-image"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-[#050508]/30 via-transparent to-transparent" />
            </div>
          </div>
          <div className="lg:col-span-5 fade-up delay-2">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ff1a8c] mb-3">Meet the trailer</p>
            <h2 className="font-script text-5xl sm:text-6xl text-white leading-[1] mb-6">
              A vintage caravan,<br />ready to wow.
            </h2>
            <p className="text-gray-300 leading-relaxed mb-5">
              Our retro silver caravan is the showpiece of every event we cater. The rounded
              teardrop silhouette, polished stainless-steel serving counter and string of warm
              fairy lights along the canopy turn it into an instant photo moment — long before the
              desserts even hit the bench.
            </p>
            <p className="text-gray-300 leading-relaxed mb-8">
              It's fully self-contained, towable to almost any flat outdoor space, and staffed by
              our own crew. Plug us in and we'll do the rest.
            </p>
            <ul data-testid="trailer-features" className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                "Self-contained — no kitchen needed",
                "Warm fairy-light canopy",
                "Twin-axle, parks on grass or paving",
                "Fully staffed and serving live",
                "Power: standard outdoor outlet",
                "Setup & pack-out included",
              ].map((feat) => (
                <li key={feat} className="flex items-start gap-2 text-gray-300">
                  <span className="text-[#00b4ff] mt-1">●</span> {feat}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* EVENT TYPES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-center mb-14 fade-up">
          <p className="text-xs uppercase tracking-[0.3em] text-[#00b4ff] mb-3">We cater</p>
          <h2 className="font-script text-5xl sm:text-6xl text-white">Any occasion, any size</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {EVENT_TYPES.map((evt, i) => {
            const Icon = evt.icon;
            return (
              <article
                key={evt.id}
                data-testid={`event-type-${evt.id}`}
                className="glass rounded-3xl border border-white/10 p-7 hover:border-[#ff1a8c]/70 hover:shadow-[0_0_28px_rgba(255,26,140,0.3)] transition-all"
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-5 ${i % 2 === 0 ? "bg-[#ff1a8c]/15 text-[#ff1a8c]" : "bg-[#00b4ff]/15 text-[#00b4ff]"}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-script text-3xl text-white mb-2">{evt.label}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{evt.blurb}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* WHAT WE BRING */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="glass rounded-3xl border border-white/10 p-10 lg:p-14 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#ff1a8c] mb-3">What we bring</p>
            <h2 className="font-script text-5xl sm:text-6xl text-white leading-[1] mb-6">
              We bring the party.
            </h2>
            <p className="text-gray-300 leading-relaxed">
              Our self-contained caravan brings the whole Just Dessert Me experience to your venue —
              fairy lights, staff and a fully custom menu tailored to your event. We arrive early,
              set up, serve your guests, and pack out so you don't lift a finger.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-5">
            {PACKAGES.map((pkg) => {
              const Icon = pkg.icon;
              return (
                <div
                  key={pkg.title}
                  data-testid={`event-package-${pkg.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl border border-white/10 p-6 bg-black/40 hover:border-[#00b4ff]/60 hover:shadow-[0_0_22px_rgba(0,180,255,0.25)] transition-all"
                >
                  <Icon className="w-7 h-7 text-[#00b4ff] mb-4" />
                  <h4 className="font-script text-2xl text-white mb-2">{pkg.title}</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">{pkg.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ENQUIRY FORM */}
      <section id="enquire" className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-center mb-12 fade-up">
          <p className="text-xs uppercase tracking-[0.3em] text-[#00b4ff] mb-3">Let's talk</p>
          <h2 className="font-script text-5xl sm:text-6xl text-white">Tell us about your event</h2>
          <p className="text-gray-400 max-w-xl mx-auto mt-4">
            Fill out the quick form and we'll get back to you within 24 hours with a tailored
            quote.
          </p>
        </div>

        <form
          data-testid="events-form"
          onSubmit={handleSubmit}
          className="glass rounded-3xl border border-white/10 p-8 lg:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
              Name
              <input data-testid="events-input-name" name="name" required value={form.name} onChange={handleChange} placeholder="Your name" className="neon-input" />
            </label>
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
              Email
              <input data-testid="events-input-email" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="you@email.com" className="neon-input" />
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
              Phone
              <input data-testid="events-input-phone" name="phone" value={form.phone} onChange={handleChange} placeholder="07 ..." className="neon-input" />
            </label>
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
              Event type
              <select data-testid="events-input-type" name="eventType" value={form.eventType} onChange={handleChange} className="neon-input">
                <option value="">Select an option…</option>
                {EVENT_TYPES.map((e) => (
                  <option key={e.id} value={e.label}>{e.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
              Event date
              <input data-testid="events-input-date" name="eventDate" type="date" value={form.eventDate} onChange={handleChange} className="neon-input" />
            </label>
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
              Guest count
              <input data-testid="events-input-guests" name="guests" type="number" min="1" value={form.guests} onChange={handleChange} placeholder="e.g. 80" className="neon-input" />
            </label>
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
              Location
              <input data-testid="events-input-location" name="location" value={form.location} onChange={handleChange} placeholder="City or venue" className="neon-input" />
            </label>
          </div>

          <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.18em] text-gray-400">
            Tell us about your event
            <textarea
              data-testid="events-input-details"
              name="details"
              value={form.details}
              onChange={handleChange}
              rows={5}
              required
              placeholder="Theme, menu ideas, dietary needs, anything we should know…"
              className="neon-input resize-none"
            />
          </label>

          {error && (
            <p data-testid="events-error" className="text-sm text-[#ff1a8c]">{error}</p>
          )}
          {success && (
            <div data-testid="events-success" className="flex items-center gap-2 text-sm text-[#00b4ff]">
              <CheckCircle2 className="w-4 h-4" /> Enquiry received — we'll be in touch within 24 hours.
            </div>
          )}

          <div className="pt-2 flex flex-wrap items-center gap-4">
            <button data-testid="events-submit-button" type="submit" disabled={loading} className="neon-btn">
              {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : "Send Enquiry"}
            </button>
            <span className="text-xs text-gray-500 flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" /> or email us at <a className="text-[#00b4ff] hover:text-white" href={`mailto:${BRAND.email}`}>{BRAND.email}</a>
            </span>
          </div>
        </form>

        <p className="text-center text-xs text-gray-500 mt-6">
          Looking for a regular dessert order instead? <Link to="/shop" className="text-[#ff1a8c] hover:text-white">Visit the shop →</Link>
        </p>
      </section>
    </main>
  );
};

export default Events;
